import { yieldToMain } from 'assets/core/js/common'

export interface Facets {
  init: () => Facets
  getFormsValues: () => {
    budgetRange: { from: number; to: number } | null
    accommodationTypes: string[]
    properties: string[]
    filters: string[]
  }
  isAccommodationMatchingForms: (accommodationId: string, includeAccommodationType?: boolean) => boolean
  isAccommodationMatchingProperties: (accommodationId: string, properties: string[]) => boolean
  isAccommodationMatchingAccommodationType: (accommodationId: string, types: string[]) => boolean
  isAccommodationMatchingFilters: (accommodationId: string, filters: string[]) => boolean
  hasOffer: (accommodationId: string) => boolean
  getAccommodationsMatchingForms: (includeAccommodationType?: boolean) => string[]
  getAccommodationTypesList: () => { name: string; types: string[] }[]
  getPropertiesList: () => string[]
  getFiltersList: () => string[]
  getCheckedFiltersList: () => HTMLInputElement[]
  sortAccommodations: (callback?: (totalMatchingAccommodations: number) => void) => Promise<void>
  updateFacets: (matchingAccommodations: string[]) => void
  updateFilter: (el: HTMLInputElement | null, value: string | number, disableField?: boolean) => void
}

export default {
  init: function (): Facets {
    return this
  },

  getFormsValues: function () {
    const budgetInput = document.querySelector<HTMLInputElement>('[data-filter="budgets"] input:checked')

    const budgetRange = budgetInput?.value.includes(':') ? budgetInput.value.split(':') : null
    const accommodationTypes = Array.from(document.querySelectorAll('[data-filter="distribution-types"] input:checked'), (e) =>
      e.getAttribute('data-distribution-types')
    )
      // @ts-expect-error no real errors
      .map((concatenatedTypes: string) => concatenatedTypes.split(';'))
      // flatten
      .reduce((acc, val) => acc.concat(val), [])

    const properties: string[] = []
    const propertiesCheckbox = document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"][data-type="property"]:checked')

    propertiesCheckbox.forEach((checkbox) => {
      properties.push(checkbox.value)
    })

    const filters: string[] = []
    const filtersCheckbox = document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"][data-type="filter"]:checked')

    filtersCheckbox.forEach((checkbox) => {
      filters.push(checkbox.getAttribute('data-name') as string)
    })

    return {
      budgetRange: budgetRange
        ? {
            from: parseInt(budgetRange[0] as string, 10),
            to: parseInt(budgetRange[1] as string, 10),
          }
        : null,
      accommodationTypes,
      properties,
      filters,
    }
  },

  isAccommodationMatchingForms: function (accommodationId, includeAccommodationType = true) {
    const formsValues = this.getFormsValues()
    const requiredBudgetRange = formsValues.budgetRange
    const requiredProperties = formsValues.properties
    const requiredFilters = formsValues.filters

    if (!(accommodationId in window.accommodationsData)) {
      return false
    }
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return
    }

    const accommodationPrice = Object.hasOwn(accommodationData, 'price') ? accommodationData.price : null

    if (!accommodationPrice) {
      return
    }

    const matchBudget = requiredBudgetRange ? accommodationPrice >= requiredBudgetRange.from && accommodationPrice <= requiredBudgetRange.to : true

    const matchProperties = this.isAccommodationMatchingProperties(accommodationId, requiredProperties)
    const matchFilters = this.isAccommodationMatchingFilters(accommodationId, requiredFilters)

    if (!includeAccommodationType) {
      return matchBudget && matchProperties && matchFilters
    }

    const requiredAccommodationTypes = formsValues.accommodationTypes

    const accommodationAccomodationTypes = Object.hasOwn(accommodationData, 'accommodationTypes') ? accommodationData.accommodationTypes : null

    if (!accommodationAccomodationTypes) {
      return
    }

    const matchAccommodationTypes =
      requiredAccommodationTypes.length > 0 ? requiredAccommodationTypes.some((x) => accommodationAccomodationTypes.includes(x)) : true

    return matchBudget && matchAccommodationTypes && matchProperties && matchFilters
  },

  isAccommodationMatchingProperties: function (accommodationId, properties) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationProperties = Object.hasOwn(accommodationData, 'properties') ? accommodationData.properties : null

    if (!accommodationProperties) {
      return false
    }

    return properties.every((elem) => accommodationProperties.indexOf(elem) > -1)
  },

  isAccommodationMatchingAccommodationType: function (accommodationId, types) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationAccomodationTypes = Object.hasOwn(accommodationData, 'accommodationTypes') ? accommodationData.accommodationTypes : null

    if (!accommodationAccomodationTypes) {
      return false
    }

    return types.some((x) => accommodationAccomodationTypes.includes(x))
  },

  isAccommodationMatchingFilters: function (accommodationId, filters) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationFilters = Object.hasOwn(accommodationData, 'filters') ? accommodationData.filters : null

    if (!accommodationFilters) {
      return false
    }

    return filters.every((elem) => accommodationFilters.indexOf(elem) > -1)
  },

  hasOffer: function (accommodationId) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationPrice = Object.hasOwn(accommodationData, 'price') ? accommodationData.price : null
    return accommodationPrice !== null
  },

  getAccommodationsMatchingForms: function (includeAccommodationType = true) {
    return window.accommodationsOrder.filter((id: string) => this.isAccommodationMatchingForms(id, includeAccommodationType))
  },

  getAccommodationTypesList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"][data-type="distribution-type"]')).map((input) => ({
      name: input.dataset.name,
      types: input.getAttribute('data-distribution-types')?.split(';'),
    }))
  },

  getPropertiesList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"][data-type="property"]')).map((input) => input.value)
  },

  getFiltersList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"][data-type="filter"]')).map(
      (input) => input.getAttribute('data-name') as string
    )
  },

  getCheckedFiltersList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="filter_offers"]:checked'))
  },

  sortAccommodations: async function (callback) {
    await yieldToMain()

    let matchingAccommodations: string[] = []
    const matchList = document.querySelector<HTMLElement>('#accommodations-match-filter .results__list')
    const noMatchList = document.querySelector<HTMLElement>('#accommodations-others .results__list')

    if (window.accommodationsData !== undefined && window.accommodationsOrder !== undefined) {
      matchingAccommodations = this.getAccommodationsMatchingForms()
    }

    if (matchList && noMatchList) {
      for (const accommodationId of window.accommodationsOrder) {
        const accommodationNode = document.querySelector(`.result__card-wrapper[data-id="${accommodationId}"]`)
        if (accommodationNode === null) {
          continue
        }

        if (matchingAccommodations.includes(accommodationId)) {
          matchList?.appendChild(accommodationNode)
        } else {
          noMatchList?.appendChild(accommodationNode)
        }
      }

      if (Array.prototype.slice.call(noMatchList.children).length === 0) {
        document.getElementById('accommodations-others')?.setAttribute('hidden', 'hidden')
      } else {
        document.getElementById('accommodations-others')?.removeAttribute('hidden')
      }
      if (Array.prototype.slice.call(matchList.children).length === 0) {
        document.getElementById('accommodations-match-filter')?.setAttribute('hidden', 'hidden')
      } else {
        document.getElementById('accommodations-match-filter')?.removeAttribute('hidden')
      }

      const totalEl = document.getElementById('accommodations-match-total')
      if (totalEl) {
        totalEl.innerHTML = Array.prototype.slice.call(matchList.children).length.toString()
      }
    } else {
      const listWrappers = Array.from(document.querySelectorAll<HTMLElement>('.results__list-wrapper'))

      if (listWrappers.length === 0) {
        return
      }

      for (const accommodationId of window.accommodationsOrder) {
        const accommodationNode = document.querySelector(`.result__card-wrapper[data-id="${accommodationId}"]`)
        if (accommodationNode === null) {
          continue
        }

        if (matchingAccommodations.includes(accommodationId)) {
          accommodationNode.removeAttribute('hidden')

          const listWrapperEl = accommodationNode.closest('.results__list-wrapper')

          if (listWrapperEl && !listWrapperEl.hasAttribute('data-show-all-results')) {
            listWrapperEl.setAttribute('data-show-all-results', 'true')
          }
        } else {
          accommodationNode.setAttribute('hidden', 'hidden')
        }
      }

      listWrappers.forEach((el) => {
        const wrapperResults = el.querySelectorAll<HTMLElement>('.result__card-wrapper')
        const totalResults = wrapperResults.length
        const totalHiddenResults = Array.from(wrapperResults).filter((element) => element.hasAttribute('hidden')).length

        if (el.hasAttribute('hidden')) {
          el.removeAttribute('hidden')
        }

        if (totalResults === totalHiddenResults) {
          el.setAttribute('hidden', 'hidden')
        }
      })

      if (this.getCheckedFiltersList().length === 0) {
        listWrappers
          .filter((el) => el.hasAttribute('data-show-all-results'))
          .forEach((el) => {
            el.removeAttribute('data-show-all-results')
          })
      }
    }

    if (callback) {
      callback(matchingAccommodations.length)
    }

    this.updateFacets(matchingAccommodations)
  },

  updateFacets: function (matchingAccommodations) {
    if (matchingAccommodations.length === 0) {
      matchingAccommodations = this.getAccommodationsMatchingForms()
    }

    for (const propertyKey of this.getPropertiesList()) {
      let facetValue = 0
      for (const accommodationId of matchingAccommodations) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingProperties(accommodationId, [propertyKey])) {
          facetValue++
        }
      }

      this.updateFilter(document.querySelector<HTMLInputElement>(`input[name^="filter_offers"][value="${propertyKey}"]`), facetValue)
    }

    for (const filterKey of this.getFiltersList()) {
      let facetValue = 0
      for (const accommodationId of matchingAccommodations) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingFilters(accommodationId, [filterKey])) {
          facetValue++
        }
      }

      this.updateFilter(document.querySelector<HTMLInputElement>(`input[name^="filter_offers"][data-name="${filterKey}"]`), facetValue)
    }

    const accommodationTypesChecked = Array.from(document.querySelectorAll('input[name="filter_offers[accommodation_types][]"]:checked'))

    for (const input of this.getAccommodationTypesList()) {
      const { types, name } = input

      if (!types) {
        return
      }

      let facetValue = 0
      for (const accommodationId of this.getAccommodationsMatchingForms(false)) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingAccommodationType(accommodationId, types)) {
          facetValue++
        }
      }

      const inputEl = document.querySelector<HTMLInputElement>(`input[name^="filter_offers"][data-name="${name}"]`)

      if (inputEl) {
        this.updateFilter(
          inputEl,
          !accommodationTypesChecked.includes(inputEl) && accommodationTypesChecked.length > 0 && facetValue > 0 ? `+${facetValue}` : facetValue,
          !accommodationTypesChecked.includes(inputEl)
        )
      }
    }
  },

  updateFilter: function (el, value, disableField = true) {
    if (!el) {
      return
    }

    const labelEl = el.nextElementSibling

    if (!labelEl) {
      return
    }

    const valueEl = labelEl.querySelector('.filter__value')

    if (!valueEl) {
      return
    }

    valueEl.textContent = value.toString()

    if (!disableField) {
      return
    }

    if (labelEl && value === 0 && !el.checked) {
      el.setAttribute('disabled', 'disabled')
      labelEl.classList.add('filter--disabled')
    } else {
      el.removeAttribute('disabled')
      labelEl.classList.remove('filter--disabled')
    }
  },
} as Facets
